import { Injectable, NgZone, OnDestroy } from '@angular/core';

/**
 * HeartbeatService
 * ----------------
 * Drives the recurring getUser / Ping cycle using a Web Worker timer so the
 * heartbeat keeps running when the tab is backgrounded or the window is
 * minimised (main-thread timers are throttled in those states).
 *
 * Design notes (performance / safety):
 *   - Exactly ONE timer exists at any time (worker-owned).
 *   - Worker messages are tiny; no application state is transferred.
 *   - Ticks are coalesced: a new tick is skipped while a request is in flight.
 *   - A watchdog auto-releases the in-flight flag if a completion signal is
 *     ever missed, so the heartbeat can never dead-lock permanently.
 *   - The Angular zone is entered only when a tick is actually actioned.
 *
 * Release contract (important when used with exhaustMap):
 *   exhaustMap silently DROPS a trigger while a request is running, so no
 *   success/error callback fires for it. The host must therefore release the
 *   guard via one of:
 *     - requestCompleted()  -> from finalize() in the RxJS pipeline
 *     - requestSkipped()    -> when a trigger is knowingly not emitted
 *     - the watchdog        -> automatic fallback for anything unforeseen
 */
@Injectable({ providedIn: 'root' })
export class HeartbeatService implements OnDestroy {

    private worker: Worker | null = null;

    /** Fallback timer used only when Web Workers are unavailable. */
    private fallbackTimer: any = null;

    /** Short retry timer used when the host reports it cannot tick yet. */
    private retryTimer: any = null;

    /** Safety net: releases inFlight if requestCompleted() is never called. */
    private watchdogTimer: any = null;

    /** Prevents overlapping requests when a response is slow. */
    private inFlight = false;

    /** Guards against double-start creating two timers. */
    private running = false;

    private intervalMs = 10000;
    private retryMs = 20000;

    /**
     * How long to wait for requestCompleted() before force-releasing the guard.
     * Must comfortably exceed the slowest expected getUser response.
     */
    private watchdogMs = 60000;

    /** Supplied by the host: performs the actual heartbeat work. */
    private onTick: (() => void) | null = null;

    /** Supplied by the host: returns true when a tick may proceed. */
    private canTick: (() => boolean) | null = null;

    private visibilityHandler = (): void => {
        if (document.visibilityState === 'visible') {
            // Re-sync immediately when the agent returns to the tab.
            this.tick();
        }
    };

    constructor(private zone: NgZone) { }

    // =========================================================
    //  LIFECYCLE
    // =========================================================

    /**
     * Start the heartbeat.
     *
     * @param onTick     Executed on every tick (push into getUserTrigger$).
     * @param canTick    Return false to skip a tick (e.g. hub not joined).
     * @param intervalMs Heartbeat period in milliseconds.
     * @param retryMs    Retry period used when canTick() returns false.
     * @param watchdogMs Force-release window for a missed completion signal.
     */
    start(
        onTick: () => void,
        canTick: () => boolean,
        intervalMs: number,
        retryMs = 20000,
        watchdogMs = 60000
    ): void {

        if (this.running) {
            return;
        }

        this.onTick = onTick;
        this.canTick = canTick;
        this.intervalMs = this.normalise(intervalMs, 10000);
        this.retryMs = this.normalise(retryMs, 20000);
        this.watchdogMs = this.normalise(watchdogMs, 60000);
        this.running = true;
        this.inFlight = false;

        this.startTimerSource();

        document.addEventListener('visibilitychange', this.visibilityHandler);

        // Fire once immediately so we do not wait a full cycle after login.
        this.tick();
    }

    /** Stop the heartbeat and release every timer/listener. */
    stop(): void {
        this.running = false;
        this.inFlight = false;

        if (this.worker) {
            this.worker.postMessage({ command: 'stop' });
            this.worker.terminate();
            this.worker = null;
        }

        if (this.fallbackTimer !== null) {
            clearInterval(this.fallbackTimer);
            this.fallbackTimer = null;
        }

        this.clearRetry();
        this.clearWatchdog();

        document.removeEventListener('visibilitychange', this.visibilityHandler);
    }

    /** Change the heartbeat period at runtime (e.g. from adjustTimer()). */
    setInterval(intervalMs: number): void {
        const next = this.normalise(intervalMs, 10000);

        if (next === this.intervalMs) {
            return;   // no-op: avoids needless timer rebuilds
        }

        this.intervalMs = next;

        if (!this.running) {
            return;
        }

        if (this.worker) {
            this.worker.postMessage({
                command: 'update',
                interval: this.intervalMs
            });
            return;
        }

        if (this.fallbackTimer !== null) {
            clearInterval(this.fallbackTimer);
        }
        this.fallbackTimer = setInterval(
            () => this.zone.run(() => this.tick()),
            this.intervalMs
        );
    }

    /**
     * Called by the host when a request settles (success OR error).
     * Safe to call more than once.
     */
    requestCompleted(): void {
        this.inFlight = false;
        this.clearWatchdog();
    }

    /**
     * Called by the host if a trigger was dropped before a request started
     * (for example when exhaustMap discards it). Releases the guard without
     * waiting for the watchdog.
     */
    requestSkipped(): void {
        this.requestCompleted();
    }

    /** True while a heartbeat request is currently outstanding. */
    isInFlight(): boolean {
        return this.inFlight;
    }

    /** True when the worker path is active (diagnostics/logging). */
    isUsingWorker(): boolean {
        return this.worker !== null;
    }

    /** True when the heartbeat is currently active. */
    isRunning(): boolean {
        return this.running;
    }

    ngOnDestroy(): void {
        this.stop();
    }

    // =========================================================
    //  INTERNALS
    // =========================================================

    private startTimerSource(): void {
        if (typeof Worker !== 'undefined') {
            try {
                this.worker = new Worker(
                    new URL('./heartbeat.worker', import.meta.url)
                );

                this.worker.onmessage = () => {
                    // Enter Angular only when a tick arrives.
                    this.zone.run(() => this.tick());
                };

                this.worker.onerror = (err) => {
                    console.error('[Heartbeat] Worker error, falling back to timer.', err);
                    this.worker?.terminate();
                    this.worker = null;
                    this.startFallbackTimer();
                };

                this.worker.postMessage({
                    command: 'start',
                    interval: this.intervalMs
                });

                return;

            } catch (err) {
                console.error('[Heartbeat] Worker creation failed, falling back.', err);
                this.worker = null;
            }
        }

        this.startFallbackTimer();
    }

    private startFallbackTimer(): void {
        if (this.fallbackTimer !== null) {
            clearInterval(this.fallbackTimer);
        }

        // Throttled in background tabs, but keeps unsupported browsers working.
        this.fallbackTimer = setInterval(
            () => this.zone.run(() => this.tick()),
            this.intervalMs
        );
    }

    /** Single entry point for every tick source (worker, fallback, visibility). */
    private tick(): void {
        if (!this.running || !this.onTick) {
            return;
        }

        if (this.inFlight) {
            return;   // coalesce: never allow overlapping requests
        }

        if (this.canTick && !this.canTick()) {
            this.scheduleRetry();
            return;
        }

        this.clearRetry();

        this.inFlight = true;
        this.startWatchdog();

        try {
            this.onTick();
        } catch (err) {
            console.error('[Heartbeat] onTick threw:', err);
            this.requestCompleted();
        }
    }

    private scheduleRetry(): void {
        if (this.retryTimer !== null) {
            return;   // a retry is already pending
        }

        this.retryTimer = setTimeout(() => {
            this.retryTimer = null;
            this.zone.run(() => this.tick());
        }, this.retryMs);
    }

    private clearRetry(): void {
        if (this.retryTimer !== null) {
            clearTimeout(this.retryTimer);
            this.retryTimer = null;
        }
    }

    private startWatchdog(): void {
        this.clearWatchdog();

        this.watchdogTimer = setTimeout(() => {
            this.watchdogTimer = null;

            if (this.inFlight) {
                console.warn(
                    '[Heartbeat] Completion signal not received within ' +
                    this.watchdogMs + 'ms. Releasing in-flight guard.'
                );
                this.inFlight = false;
            }
        }, this.watchdogMs);
    }

    private clearWatchdog(): void {
        if (this.watchdogTimer !== null) {
            clearTimeout(this.watchdogTimer);
            this.watchdogTimer = null;
        }
    }

    private normalise(value: number, fallback: number): number {
        return Number.isFinite(value) && value >= 1000 ? value : fallback;
    }
}
